import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Trash2, X, ChevronDown, ChevronUp, Calendar, CheckSquare, Square, Search, User, Copy, Edit
} from "lucide-react";
import { toast } from "sonner";

// Searchable workout selector for program builder
//
// Best-match ranking:
//   - Query is split into whitespace tokens (e.g., "sach lower" → ["sach", "lower"])
//   - Every token must appear somewhere in the title (case-insensitive)
//   - Higher score:
//       prefix match on full query (title starts with the typed string) = +1000
//       each token matching at a word boundary                          = +100
//       each token matching anywhere                                    = +10
//   - Ties broken alphabetically by title
const scoreWorkout = (title, query) => {
  if (!query) return 0;
  const t = (title || "").toLowerCase();
  const q = query.toLowerCase().trim();
  if (!q) return 0;
  if (t.startsWith(q)) return 1000;

  const tokens = q.split(/\s+/).filter(Boolean);
  let score = 0;
  for (const tok of tokens) {
    // Word-boundary match (start of title or after a space/dash/paren/digit)
    const wbRegex = new RegExp(`(^|[\\s\\-()_])${tok.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`, "i");
    if (wbRegex.test(t)) {
      score += 100;
    } else if (t.includes(tok)) {
      score += 10;
    } else {
      // a token doesn't match at all — disqualify this workout
      return 0;
    }
  }
  return score;
};

const WorkoutSearchSelector = ({ workouts, selectedWorkoutId, onSelect, testId }) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  // Find selected workout title for display
  const selectedWorkout = workouts.find(w => w.workout_id === selectedWorkoutId);

  // Rank-and-filter workouts by query — fuzzy token match with score-based ordering
  const filtered = query.trim()
    ? workouts
        .map(w => ({ w, score: scoreWorkout(w.title, query) }))
        .filter(x => x.score > 0)
        .sort((a, b) => b.score - a.score || (a.w.title || "").localeCompare(b.w.title || ""))
        .map(x => x.w)
    : workouts;

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={ref} className="relative" data-testid={testId}>
      <div
        className="w-full bg-[#1E293B] border border-[#1E293B] rounded-lg px-3 py-2 text-sm flex items-center gap-2 cursor-pointer hover:border-[#475569] transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Search className="w-3.5 h-3.5 text-gray-500 shrink-0" />
        {isOpen ? (
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search workouts..."
            className="flex-1 bg-transparent text-white outline-none placeholder-gray-500 text-sm"
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className={`flex-1 truncate ${selectedWorkout ? "text-white" : "text-gray-500"}`}>
            {selectedWorkout ? selectedWorkout.title : "Search workouts..."}
          </span>
        )}
        {selectedWorkout && !isOpen && (
          <button type="button" onClick={(e) => { e.stopPropagation(); onSelect(""); setQuery(""); }}
            className="p-0.5 hover:bg-[#374B69] rounded">
            <X className="w-3 h-3 text-gray-400" />
          </button>
        )}
        <ChevronDown className={`w-3.5 h-3.5 text-gray-500 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-[#1A2235] border border-[#2D3B4F] rounded-lg shadow-xl max-h-48 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">No workouts found</div>
          ) : (
            filtered.map((w) => (
              <button
                key={w.workout_id}
                type="button"
                onClick={() => { onSelect(w.workout_id); setQuery(""); setIsOpen(false); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-[#2D3B4F] transition-colors flex items-center justify-between ${
                  w.workout_id === selectedWorkoutId ? "text-primary bg-[#1E293B]" : "text-gray-300"
                }`}
              >
                <span className="truncate">{w.title}</span>
                {w.workout_id === selectedWorkoutId && <CheckSquare className="w-3.5 h-3.5 text-primary shrink-0 ml-2" />}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};
import { useViewPreferences, ViewControls, sortItems, BulkDeleteModal, SelectionHeader, API } from "./SharedComponents";
import { SubtabManager, SubtabSelector } from "./SubtabManager";

// ==================== PROGRAM LIBRARY ====================
const ProgramLibrary = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const { viewMode, setViewMode, sortMode, setSortMode } = useViewPreferences("programs");
  
  // Filter state - category tabs
  const [categoryFilter, setCategoryFilter] = useState("");
  const [subtabFilter, setSubtabFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Program categories (matching your 6 S's)
  const programCategories = [
    { value: "", label: "All" },
    { value: "speed", label: "Speed" },
    { value: "strength", label: "Strength" },
    { value: "stamina", label: "Stamina" },
    { value: "suppleness", label: "Suppleness" },
    { value: "skills", label: "Skills" },
    { value: "extras", label: "Extras" },
  ];
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/coach/programs`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setPrograms(data.programs);
      }
    } catch (error) {
      toast.error("Failed to load programs");
    } finally {
      setLoading(false);
    }
  };

  const handleClone = async (program) => {
    try {
      const response = await fetch(`${API}/coach/programs/${program.program_id}/clone`, {
        method: "POST",
        credentials: "include"
      });
      if (response.ok) {
        toast.success(`Program "${program.title}" cloned successfully!`);
        fetchPrograms();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to clone program");
      }
    } catch (error) {
      toast.error("Failed to clone program");
    }
  };

  const handleDelete = async (programId) => {
    if (!confirm("Are you sure you want to delete this program?")) return;
    try {
      const response = await fetch(`${API}/coach/programs/${programId}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (response.ok) {
        toast.success("Program deleted");
        setSelectedIds(prev => { const next = new Set(prev); next.delete(programId); return next; });
        fetchPrograms();
      }
    } catch (error) {
      toast.error("Failed to delete program");
    }
  };

  // Bulk selection handlers
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === programs.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(programs.map(p => p.program_id)));
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    let successCount = 0;
    for (const id of selectedIds) {
      try {
        const response = await fetch(`${API}/coach/programs/${id}`, {
          method: "DELETE",
          credentials: "include"
        });
        if (response.ok) successCount++;
      } catch (error) {
        console.error(`Failed to delete program ${id}:`, error);
      }
    }
    setBulkDeleting(false);
    setShowBulkDeleteModal(false);
    setSelectedIds(new Set());
    toast.success(`Deleted ${successCount} program${successCount !== 1 ? 's' : ''}`);
    fetchPrograms();
  };

  const selectedPrograms = programs.filter(p => selectedIds.has(p.program_id));
  
  // Filter programs by category, subtab, and search
  const filteredPrograms = React.useMemo(() => {
    return programs.filter(program => {
      const matchesCategory = !categoryFilter || program.workout_type === categoryFilter;
      const matchesSubtab = !subtabFilter || (program.subtabs && program.subtabs.includes(subtabFilter));
      const matchesSearch = !searchQuery || 
        program.title?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSubtab && matchesSearch;
    });
  }, [programs, categoryFilter, subtabFilter, searchQuery]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-bold">Program Library</h2>
          <p className="text-gray-400">Design multi-week training programs</p>
        </div>
        <div className="flex items-center gap-4">
          <ViewControls 
            viewMode={viewMode} 
            setViewMode={setViewMode}
            sortMode={sortMode}
            setSortMode={setSortMode}
          />
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-600 transition-colors"
            data-testid="create-program-btn"
          >
            <Plus className="w-5 h-5" />
            Create Program
          </button>
        </div>
      </div>

      {/* Category Filter Tabs */}
      <div className="flex flex-wrap items-center gap-2">
        {programCategories.map((cat) => (
          <button
            key={cat.value}
            onClick={() => { setCategoryFilter(cat.value); setSubtabFilter(""); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              categoryFilter === cat.value
                ? "bg-primary text-white"
                : "bg-[#1E293B] text-gray-400 hover:bg-[#2D3B4F]"
            }`}
            data-testid={`filter-category-${cat.value || 'all'}`}
          >
            {cat.label}
          </button>
        ))}
        
        {/* Search */}
        <div className="relative ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            placeholder="Search programs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 bg-[#1E293B] border border-[#1E293B] rounded-lg text-sm focus:border-primary outline-none w-48"
            data-testid="search-programs"
          />
        </div>
      </div>

      {/* Subtabs (only shown when a category is selected) */}
      <SubtabManager
        libraryType="programs"
        selectedCategory={categoryFilter}
        selectedSubtab={subtabFilter}
        onSubtabSelect={setSubtabFilter}
      />

      {/* Selection Header */}
      {filteredPrograms.length > 0 && (
        <SelectionHeader
          selectedCount={selectedIds.size}
          totalCount={filteredPrograms.length}
          onSelectAll={selectAll}
          onClearSelection={clearSelection}
          onDeleteSelected={() => setShowBulkDeleteModal(true)}
          allSelected={selectedIds.size === filteredPrograms.length && filteredPrograms.length > 0}
        />
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : filteredPrograms.length > 0 ? (
        viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortItems(filteredPrograms, sortMode, "title").map((program) => (
              <ProgramCard
                key={program.program_id}
                program={program}
                selected={selectedIds.has(program.program_id)}
                onToggleSelect={() => toggleSelect(program.program_id)}
                onDelete={() => handleDelete(program.program_id)}
                onClone={() => handleClone(program)}
                onEdit={() => setEditingProgram(program)}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {sortItems(filteredPrograms, sortMode, "title").map((program) => (
              <ProgramListItem
                key={program.program_id}
                program={program}
                selected={selectedIds.has(program.program_id)}
                onToggleSelect={() => toggleSelect(program.program_id)}
                onDelete={() => handleDelete(program.program_id)}
                onClone={() => handleClone(program)}
                onEdit={() => setEditingProgram(program)}
              />
            ))}
          </div>
        )
      ) : (
        <div className="text-center py-12 bg-[#141B2D] border border-[#1E293B] rounded-xl">
          <Calendar className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <p className="text-gray-400">
            {(categoryFilter || subtabFilter || searchQuery) ? `No programs match your filters (${programs.length} total)` : "No programs created yet"}
          </p>
          {(categoryFilter || subtabFilter || searchQuery) && (
            <button
              onClick={() => { setCategoryFilter(""); setSubtabFilter(""); setSearchQuery(""); }}
              className="mt-3 px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-blue-600 transition-colors"
              data-testid="clear-all-program-filters-btn"
            >
              Clear All Filters
            </button>
          )}
          {!categoryFilter && !subtabFilter && !searchQuery && (
            <button
              onClick={() => setShowForm(true)}
              className="mt-4 text-primary hover:underline"
            >
              Create your first program
            </button>
          )}
        </div>
      )}

      {/* Bulk Delete Modal */}
      <AnimatePresence>
        {showBulkDeleteModal && (
          <BulkDeleteModal
            items={selectedPrograms}
            itemType="Program"
            nameField="title"
            onConfirm={handleBulkDelete}
            onCancel={() => setShowBulkDeleteModal(false)}
            deleting={bulkDeleting}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showForm && (
          <ProgramForm
            onClose={() => setShowForm(false)}
            onSave={() => { setShowForm(false); fetchPrograms(); }}
          />
        )}
      </AnimatePresence>

      {/* Edit Program Modal */}
      <AnimatePresence>
        {editingProgram && (
          <ProgramForm
            program={editingProgram}
            onClose={() => setEditingProgram(null)}
            onSave={() => { setEditingProgram(null); fetchPrograms(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const ProgramCard = ({ program, selected, onToggleSelect, onDelete, onClone, onEdit }) => (
  <div 
    className={`bg-[#141B2D] border rounded-xl overflow-hidden hover:border-primary/50 transition-all cursor-pointer ${selected ? 'border-primary ring-2 ring-primary/30' : 'border-[#1E293B]'}`} 
    data-testid={`program-card-${program.program_id}`}
    onClick={onEdit}
  >
    <div className="aspect-video bg-[#1E293B] relative">
      {program.thumbnail_url ? (
        <img src={program.thumbnail_url} alt={program.title} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-accent/20">
          <Calendar className="w-16 h-16 text-gray-500" />
        </div>
      )}
      <div className="absolute bottom-2 right-2 bg-black/80 px-2 py-1 rounded">
        <span className="text-xs">{program.duration_weeks} weeks</span>
      </div>
      {/* Selection Checkbox */}
      <button
        onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
        className={`absolute top-2 left-2 w-6 h-6 rounded flex items-center justify-center transition-colors ${
          selected ? 'bg-primary text-white' : 'bg-black/60 hover:bg-black/80 text-white'
        }`}
        data-testid={`select-program-${program.program_id}`}
      >
        {selected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
      </button>
    </div>
    <div className="p-4">
      <div className="flex items-start justify-between mb-2">
        <h3 className="font-heading text-lg font-bold">{program.title}</h3>
        <div className="flex items-center gap-1">
          <button onClick={(e) => { e.stopPropagation(); onEdit(); }} className="p-1 hover:bg-[#1E293B] rounded text-blue-400" data-testid={`edit-program-${program.program_id}`} title="Edit program">
            <Edit className="w-4 h-4" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onClone(); }} className="p-1 hover:bg-[#1E293B] rounded text-primary" data-testid={`clone-program-${program.program_id}`} title="Clone program">
            <Copy className="w-4 h-4" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="p-1 hover:bg-[#1E293B] rounded text-red-400" data-testid={`delete-program-${program.program_id}`} title="Delete program">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      <p className="text-gray-400 text-sm mb-3 line-clamp-2">{program.description}</p>
      <div className="flex flex-wrap gap-2">
        <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded capitalize">
          {(program.workout_type || program.goal)?.replace("_", " ")}
        </span>
        <span className="text-xs bg-lime-400/20 text-lime-300 px-2 py-1 rounded">
          {program.days_per_week} days/week
        </span>
        <span className="text-xs bg-[#1E293B] text-gray-400 px-2 py-1 rounded capitalize">
          {program.difficulty}
        </span>
      </div>
    </div>
  </div>
);

// Program List Item (for list view)
const ProgramListItem = ({ program, selected, onToggleSelect, onDelete, onClone, onEdit }) => (
  <div 
    className={`bg-[#141B2D] border rounded-lg p-4 hover:border-primary/50 transition-all flex items-center gap-4 cursor-pointer ${selected ? 'border-primary ring-2 ring-primary/30' : 'border-[#1E293B]'}`} 
    data-testid={`program-list-${program.program_id}`}
    onClick={onEdit}
  >
    {/* Selection Checkbox */}
    <button
      onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
      className={`w-6 h-6 rounded flex items-center justify-center transition-colors flex-shrink-0 ${
        selected ? 'bg-primary text-white' : 'bg-[#1E293B] hover:bg-[#2D3B4F] text-white'
      }`}
      data-testid={`select-program-list-${program.program_id}`}
    >
      {selected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
    </button>
    
    {/* Thumbnail */}
    <div className="w-20 h-14 bg-[#1E293B] rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden">
      {program.thumbnail_url ? (
        <img src={program.thumbnail_url} alt={program.title} className="w-full h-full object-cover" />
      ) : (
        <Calendar className="w-6 h-6 text-gray-500" />
      )}
    </div>
    
    {/* Info */}
    <div className="flex-1 min-w-0">
      <h3 className="font-semibold text-white truncate mb-1">{program.title}</h3>
      <div className="flex flex-wrap gap-2">
        <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded capitalize">
          {(program.workout_type || program.goal)?.replace("_", " ")}
        </span>
        <span className="text-xs bg-lime-400/20 text-lime-300 px-2 py-0.5 rounded">
          {program.days_per_week} days/week
        </span>
        <span className="text-xs bg-[#2D3B4F] text-gray-400 px-2 py-0.5 rounded">
          {program.duration_weeks} weeks
        </span>
        <span className="text-xs bg-[#1E293B] text-gray-400 px-2 py-0.5 rounded capitalize">
          {program.difficulty}
        </span>
      </div>
    </div>
    
    {/* Actions */}
    <div className="flex items-center gap-2 flex-shrink-0">
      <button onClick={(e) => { e.stopPropagation(); onEdit(); }} className="p-2 hover:bg-[#1E293B] rounded-lg text-blue-400" data-testid={`edit-program-list-${program.program_id}`} title="Edit program">
        <Edit className="w-4 h-4" />
      </button>
      <button onClick={(e) => { e.stopPropagation(); onClone(); }} className="p-2 hover:bg-[#1E293B] rounded-lg text-primary" data-testid={`clone-program-list-${program.program_id}`} title="Clone program">
        <Copy className="w-4 h-4" />
      </button>
      <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="p-2 hover:bg-[#1E293B] rounded-lg text-red-400" data-testid={`delete-program-list-${program.program_id}`} title="Delete program">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  </div>
);

// ==================== PROGRAM FORM (Simplified) ====================
const ProgramForm = ({ program, onClose, onSave }) => {
  const isEditing = !!program;
  const [formData, setFormData] = useState({
    title: program?.title || "",
    description: program?.description || "",
    goal: program?.goal || program?.workout_type || "general_fitness",
    workout_type: program?.workout_type || "strength",
    difficulty: program?.difficulty || "intermediate",
    tier_required: program?.tier_required || "foundations",
    allowed_tiers: program?.allowed_tiers || (program?.tier_required ? [program.tier_required] : ["foundations"]),
    duration_weeks: program?.duration_weeks || program?.weeks?.length || 4,
    days_per_week: program?.days_per_week || 5,
    weeks: program?.weeks || [],
    assigned_users: program?.assigned_users || [],
    subtabs: program?.subtabs || [],
  });
  const [workouts, setWorkouts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [showUserSearch, setShowUserSearch] = useState(false);

  // Fetch workouts on mount
  useEffect(() => {
    const fetchWorkouts = async () => {
      try {
        // Fetch all workouts (up to 500) for the program builder
        const response = await fetch(`${API}/coach/workout-templates?limit=500`, { credentials: "include" });
        if (response.ok) {
          const data = await response.json();
          setWorkouts(data.templates || []);
        }
      } catch (error) {
        console.error("Failed to fetch workouts");
      }
    };
    fetchWorkouts();
  }, []);

  // Fetch users when "Specific Users" tier is selected
  useEffect(() => {
    if ((formData.allowed_tiers || [formData.tier_required]).includes("specific_users")) {
      fetchUsers();
    }
  }, [formData.tier_required]);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/admin/users?limit=500`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error("Failed to fetch users");
    }
  };

  // Filter users based on search
  const filteredUsers = users.filter(user => {
    if (!userSearch.trim()) return true;
    const search = userSearch.toLowerCase();
    return (
      user.name?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search)
    );
  });

  // Add user to assigned list
  const addUser = (user) => {
    if (!formData.assigned_users.find(u => u.user_id === user.user_id)) {
      setFormData(prev => ({
        ...prev,
        assigned_users: [...prev.assigned_users, { user_id: user.user_id, name: user.name, email: user.email }]
      }));
    }
    setUserSearch("");
    setShowUserSearch(false);
  };

  // Remove user from assigned list
  const removeUser = (userId) => {
    setFormData(prev => ({
      ...prev,
      assigned_users: prev.assigned_users.filter(u => u.user_id !== userId)
    }));
  };

  // Auto-generate weeks when duration changes
  useEffect(() => {
    if (formData.duration_weeks > 0) {
      setFormData(prev => {
        const weeks = [];
        for (let w = 1; w <= prev.duration_weeks; w++) {
          const existingWeek = prev.weeks[w - 1];
          weeks.push({
            week_number: w,
            week_name: `Week ${w}`,
            days: existingWeek?.days || [{ day_number: 1, day_name: "Day 1", workout_id: null, notes: null }],
            notes: existingWeek?.notes || null
          });
        }
        return { ...prev, weeks };
      });
    }
  }, [formData.duration_weeks]);

  // Update number of days in a week
  const updateDaysCount = (weekIndex, newCount) => {
    const count = Math.max(1, Math.min(7, newCount));
    setFormData(prev => {
      const newWeeks = prev.weeks.map((w, i) => {
        if (i !== weekIndex) return w;
        const currentDays = [...w.days];
        if (count > currentDays.length) {
          for (let d = currentDays.length + 1; d <= count; d++) {
            currentDays.push({ day_number: d, day_name: `Day ${d}`, workout_id: null, notes: null });
          }
        } else if (count < currentDays.length) {
          return { ...w, days: currentDays.slice(0, count) };
        }
        return { ...w, days: currentDays };
      });
      return { ...prev, weeks: newWeeks };
    });
  };

  // Update day workout assignment
  const updateDayWorkout = (weekIndex, dayIndex, workoutId) => {
    const workout = workoutId ? workouts.find(w => w.workout_id === workoutId) : null;
    setFormData(prev => {
      const newWeeks = prev.weeks.map((w, wi) => {
        if (wi !== weekIndex) return w;
        const newDays = w.days.map((d, di) => {
          if (di !== dayIndex) return d;
          return { ...d, workout_id: workoutId || null, workout_title: workout?.title || null };
        });
        return { ...w, days: newDays };
      });
      return { ...prev, weeks: newWeeks };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = isEditing 
        ? `${API}/coach/programs/${program.program_id}` 
        : `${API}/coach/programs`;
      const method = isEditing ? "PUT" : "POST";
      
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(isEditing ? "Program updated!" : "Program created!");
        onSave();
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || (isEditing ? "Failed to update program" : "Failed to create program"));
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[#141B2D] border border-[#1E293B] rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[#141B2D] border-b border-[#1E293B] p-6 flex justify-between items-center z-10">
          <h2 className="font-heading text-2xl font-bold">{isEditing ? "Edit Program" : "Create Program"}</h2>
          <button onClick={onClose} className="p-2 hover:bg-[#1E293B] rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Row 1: Program Name | Workout Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Program Name *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => { const v = e.target.value; setFormData(prev => ({ ...prev, title: v })); }}
                placeholder="Enter program name"
                className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-3 text-white focus:border-primary outline-none"
                required
                data-testid="program-title-input"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Workout Type</label>
              <select
                value={formData.workout_type}
                onChange={(e) => { const v = e.target.value; setFormData(prev => ({ ...prev, workout_type: v, subtabs: [] })); }}
                className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-3 text-white focus:border-primary outline-none"
                data-testid="workout-type-select"
              >
                <option value="speed">Speed</option>
                <option value="strength">Strength</option>
                <option value="stamina">Stamina</option>
                <option value="suppleness">Suppleness</option>
                <option value="skill">Skill</option>
                <option value="extras">Extras</option>
              </select>
            </div>
          </div>

          {/* Subtabs for Program Organization */}
          <SubtabSelector
            libraryType="programs"
            category={formData.workout_type || formData.goal}
            selectedSubtabs={formData.subtabs}
            onChange={(subtabs) => setFormData(prev => ({ ...prev, subtabs }))}
          />

          {/* Row 2: Tier | How many weeks? */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Who can access this program?</label>
              <div className="space-y-2" data-testid="tier-checkboxes">
                {[
                  { value: "just_for_me", label: "Just for Me (Coach Only)" },
                  { value: "specific_users", label: "Specific Users" },
                  { value: "foundations", label: "Foundations" },
                  { value: "accelerator", label: "Advanced Accelerator" },
                  { value: "elite", label: "Elite" },
                ].map(opt => {
                  const tiers = formData.allowed_tiers || (formData.tier_required ? [formData.tier_required] : ["foundations"]);
                  const checked = tiers.includes(opt.value);
                  const toggle = () => {
                    let next;
                    if (opt.value === "just_for_me" || opt.value === "specific_users") {
                      next = [opt.value];
                    } else if (checked) {
                      next = tiers.filter(t => t !== opt.value && t !== "just_for_me" && t !== "specific_users");
                      if (next.length === 0) next = ["foundations"];
                    } else {
                      next = [...tiers.filter(t => t !== "just_for_me" && t !== "specific_users"), opt.value];
                    }
                    setFormData(prev => ({ ...prev, allowed_tiers: next, tier_required: next[0], assigned_users: opt.value !== "specific_users" ? [] : prev.assigned_users }));
                  };
                  return (
                    <label key={opt.value} className="flex items-center gap-3 cursor-pointer py-1.5 px-3 rounded-lg hover:bg-white/5 transition-colors">
                      <input type="checkbox" checked={checked} onChange={toggle}
                        className="w-4 h-4 rounded border-[#2D3B4F] bg-[#0B0F1A] text-blue-500 focus:ring-blue-500 cursor-pointer" />
                      <span className="text-sm text-white">{opt.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">How many weeks?</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={formData.duration_weeks}
                  onChange={(e) => { const v = Math.max(1, Math.min(52, parseInt(e.target.value) || 1)); setFormData(prev => ({ ...prev, duration_weeks: v })); }}
                  min={1}
                  max={52}
                  className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-3 text-white focus:border-primary outline-none"
                  data-testid="weeks-input"
                />
                <div className="flex flex-col gap-0.5">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, duration_weeks: Math.min(52, prev.duration_weeks + 1) }))}
                    className="p-1 bg-[#2D3B4F] hover:bg-[#374B69] rounded text-white"
                    data-testid="weeks-up"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, duration_weeks: Math.max(1, prev.duration_weeks - 1) }))}
                    className="p-1 bg-[#2D3B4F] hover:bg-[#374B69] rounded text-white"
                    data-testid="weeks-down"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* User Assignment Section (when "Specific Users" is checked) */}
          {(formData.allowed_tiers || [formData.tier_required]).includes("specific_users") && (
            <div className="bg-[#0F1419] border border-[#1E293B] rounded-xl p-4">
              <label className="block text-sm text-gray-400 mb-3">Assign to Specific Users</label>
              
              {/* Search Input */}
              <div className="relative mb-3">
                <input
                  type="text"
                  value={userSearch}
                  onChange={(e) => { setUserSearch(e.target.value); setShowUserSearch(true); }}
                  onFocus={() => setShowUserSearch(true)}
                  placeholder="Search by name or email..."
                  className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-4 py-2 text-white focus:border-primary outline-none"
                  data-testid="user-search-input"
                />
                
                {/* Search Results Dropdown */}
                {showUserSearch && userSearch.trim() && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-[#0B0F1A] border border-[#1E293B] rounded-lg max-h-48 overflow-y-auto z-20">
                    {filteredUsers.length > 0 ? (
                      filteredUsers.slice(0, 10).map(user => (
                        <button
                          key={user.user_id}
                          type="button"
                          onClick={() => addUser(user)}
                          className="w-full px-4 py-2 text-left hover:bg-[#1E293B] flex items-center justify-between"
                          data-testid={`user-option-${user.user_id}`}
                        >
                          <div>
                            <p className="text-white font-medium">{user.name || 'Unnamed'}</p>
                            <p className="text-xs text-gray-400">{user.email}</p>
                          </div>
                          <span className="text-xs text-gray-500 capitalize">{user.subscription_tier || 'free'}</span>
                        </button>
                      ))
                    ) : (
                      <p className="px-4 py-3 text-gray-400 text-sm">No users found</p>
                    )}
                  </div>
                )}
              </div>

              {/* Assigned Users List */}
              {formData.assigned_users.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {formData.assigned_users.map(user => (
                    <div
                      key={user.user_id}
                      className="flex items-center gap-2 bg-primary/20 border border-primary/30 text-primary px-3 py-1.5 rounded-lg"
                    >
                      <span className="text-sm">{user.name || user.email}</span>
                      <button
                        type="button"
                        onClick={() => removeUser(user.user_id)}
                        className="hover:text-white"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No users assigned yet. Search and select users above.</p>
              )}
            </div>
          )}

          {/* Info text for tier selection */}
          <div className="text-sm text-gray-500">
            {(() => {
              const tiers = formData.allowed_tiers || [formData.tier_required];
              if (tiers.includes("just_for_me")) return <p>This program will only be visible to you (the coach). Perfect for testing or personal use.</p>;
              if (tiers.includes("specific_users")) return <p>This program will only be visible to the users you assign above.</p>;
              const names = tiers.map(t => ({ foundations: "Foundations", accelerator: "Advanced Accelerator", elite: "Elite" }[t])).filter(Boolean);
              if (names.length > 0) return <p>This program will be available to: <span className="text-white font-medium">{names.join(", ")}</span> subscribers.</p>;
              return null;
            })()}
          </div>

          {/* Weeks Section */}
          {formData.weeks.length > 0 && (
            <div className="space-y-4">
              <h3 className="font-heading text-lg font-bold border-b border-[#1E293B] pb-2">Weeks</h3>
              {formData.weeks.map((week, weekIndex) => (
                <div key={weekIndex} className="bg-[#0B0F1A] border border-[#1E293B] rounded-xl p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-primary">{week.week_name}</h4>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-400">Days:</span>
                      <button
                        type="button"
                        onClick={() => updateDaysCount(weekIndex, week.days.length - 1)}
                        disabled={week.days.length <= 1}
                        className="w-7 h-7 bg-[#2D3B4F] hover:bg-[#374B69] disabled:opacity-50 disabled:cursor-not-allowed rounded flex items-center justify-center"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <span className="w-8 text-center font-bold">{week.days.length}</span>
                      <button
                        type="button"
                        onClick={() => updateDaysCount(weekIndex, week.days.length + 1)}
                        disabled={week.days.length >= 7}
                        className="w-7 h-7 bg-[#2D3B4F] hover:bg-[#374B69] disabled:opacity-50 disabled:cursor-not-allowed rounded flex items-center justify-center"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Day Workout Assignments */}
                  <div className="flex flex-col gap-2">
                    {week.days.map((day, dayIndex) => (
                      <div key={dayIndex} className="flex items-center gap-3">
                        <label className="text-xs text-gray-500 w-10 shrink-0">Day {dayIndex + 1}</label>
                        <div className="flex-1">
                          <WorkoutSearchSelector
                            workouts={workouts}
                            selectedWorkoutId={day.workout_id || ""}
                            onSelect={(workoutId) => updateDayWorkout(weekIndex, dayIndex, workoutId)}
                            testId={`day-workout-${weekIndex}-${dayIndex}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Submit */}
          <div className="flex gap-4 pt-4 border-t border-[#1E293B]">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-[#1E293B] text-white py-3 rounded-lg font-semibold hover:bg-[#2D3B4F] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.title.trim()}
              className="flex-1 bg-primary text-white py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors disabled:opacity-50"
              data-testid="save-program-btn"
            >
              {saving ? "Saving..." : "Create Program"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default ProgramLibrary;
